"use client"

import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "@/lib/utils"

type AgentNarrativeMarkdownProps = {
  className?: string
  children: string
}

/** Renders orchestrator / execution narrative text (often markdown) with dashboard-consistent typography. */
export function AgentNarrativeMarkdown({ className, children }: AgentNarrativeMarkdownProps) {
  return (
    <div className={cn("min-w-0 text-foreground", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          h1: ({ node: _n, className: c, ...props }) => (
            <h1
              className={cn(
                "mt-4 mb-2 border-b border-border pb-1.5 text-base font-semibold tracking-tight first:mt-0",
                c,
              )}
              {...props}
            />
          ),
          h2: ({ node: _n, className: c, ...props }) => (
            <h2 className={cn("mt-3 mb-1.5 text-sm font-semibold tracking-tight", c)} {...props} />
          ),
          h3: ({ node: _n, className: c, ...props }) => (
            <h3 className={cn("mt-2.5 mb-1 text-sm font-semibold text-foreground/95", c)} {...props} />
          ),
          h4: ({ node: _n, className: c, ...props }) => (
            <h4 className={cn("mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground", c)} {...props} />
          ),
          p: ({ node: _n, className: c, ...props }) => (
            <p className={cn("mb-2 text-sm leading-relaxed text-foreground/90 last:mb-0", c)} {...props} />
          ),
          ul: ({ node: _n, className: c, ...props }) => (
            <ul className={cn("mb-2 list-disc space-y-1 pl-4 text-sm leading-relaxed marker:text-muted-foreground", c)} {...props} />
          ),
          ol: ({ node: _n, className: c, ...props }) => (
            <ol className={cn("mb-2 list-decimal space-y-1 pl-4 text-sm leading-relaxed marker:text-muted-foreground", c)} {...props} />
          ),
          li: ({ node: _n, className: c, ...props }) => <li className={cn("pl-0.5", c)} {...props} />,
          strong: ({ node: _n, className: c, ...props }) => (
            <strong className={cn("font-semibold text-foreground", c)} {...props} />
          ),
          em: ({ node: _n, className: c, ...props }) => <em className={cn("italic text-foreground/90", c)} {...props} />,
          blockquote: ({ node: _n, className: c, ...props }) => (
            <blockquote
              className={cn(
                "my-2 border-l-2 border-primary/50 bg-primary/[0.06] py-1.5 pl-3 pr-2 text-sm italic text-muted-foreground",
                c,
              )}
              {...props}
            />
          ),
          hr: ({ node: _n, className: c, ...props }) => (
            <hr className={cn("my-4 border-0 border-t border-border", c)} {...props} />
          ),
          a: ({ node: _n, className: c, href, ...props }) => {
            const external = typeof href === "string" && /^https?:\/\//i.test(href)
            return (
              <a
                href={href}
                className={cn("font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:text-primary/90", c)}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                {...props}
              />
            )
          },
          code: ({ node: _n, className: c, children, ...props }) => {
            const isBlock = typeof c === "string" && c.includes("language-")
            if (isBlock) {
              return (
                <code className={cn("block font-mono text-[12px] leading-relaxed text-foreground", c)} {...props}>
                  {children}
                </code>
              )
            }
            return (
              <code
                className={cn(
                  "rounded border border-border/80 bg-muted/80 px-1.5 py-0.5 font-mono text-[12px] text-foreground",
                  c,
                )}
                {...props}
              >
                {children}
              </code>
            )
          },
          pre: ({ node: _n, className: c, children, ...props }) => (
            <pre
              className={cn(
                "mb-2 overflow-x-auto rounded-md border border-border bg-muted/35 p-3 text-[12px] leading-relaxed",
                c,
              )}
              {...props}
            >
              {children}
            </pre>
          ),
          table: ({ node: _n, className: c, children, ...props }) => (
            <div className="mb-2 max-w-full overflow-x-auto rounded-md border border-border">
              <table className={cn("w-full min-w-[240px] border-collapse text-left text-xs", c)} {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ node: _n, className: c, ...props }) => (
            <thead className={cn("border-b border-border bg-muted/40", c)} {...props} />
          ),
          tbody: ({ node: _n, className: c, ...props }) => <tbody className={cn("divide-y divide-border/80", c)} {...props} />,
          tr: ({ node: _n, className: c, ...props }) => <tr className={cn("", c)} {...props} />,
          th: ({ node: _n, className: c, ...props }) => (
            <th className={cn("border border-border/60 px-2 py-1.5 font-semibold text-foreground", c)} {...props} />
          ),
          td: ({ node: _n, className: c, ...props }) => (
            <td className={cn("border border-border/60 px-2 py-1.5 align-top text-muted-foreground", c)} {...props} />
          ),
          del: ({ node: _n, className: c, ...props }) => (
            <del className={cn("text-muted-foreground line-through", c)} {...props} />
          ),
          input: ({ node: _n, className: c, ...props }) => (
            <input className={cn("mr-1.5 size-3.5 shrink-0 accent-primary", c)} {...props} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
